import styled from "styled-components";
import { useAccount, useConnect } from 'wagmi';
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo } from "react";

import Popup from "./Popup";
import { selectConnectingWallet, walletConnected, connectWallet } from "../state/uiSlice";
import Button from "./Button";

const Buttons = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const InjectedWallets = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  margin-bottom: 0.6rem;
`;

const WalletConnectSection = styled.div`
`;

const WalletSelector = (): JSX.Element => {
  const dispatch = useDispatch();
  const { address, isConnected } = useAccount();
  const { connectors, connect, error: connectError, isPending } = useConnect();

  const connectingWallet = useSelector(selectConnectingWallet);

  // Force popup to close when wallet is connected (memoized to prevent re-renders)
  const shouldShowPopup = useMemo(() => {
    return connectingWallet || (!isConnected && !address);
  }, [connectingWallet, isConnected, address]);

  // Debug: log all available connectors (only when connectors change)
  useEffect(() => {
    console.log('Available connectors:', connectors.map(c => ({ name: c.name, id: c.id })));
  }, [connectors]);

  // Auto-close popup when wallet connects successfully
  useEffect(() => {
    console.log('Connection state changed:', { 
      isConnected, 
      connectingWallet, 
      address, 
      hasAddress: !!address,
      popupShouldShow: shouldShowPopup
    });
    
    if (isConnected && address) {
      console.log('✅ Wallet connected successfully, closing popup immediately');
      dispatch(walletConnected());
    } else if (!isConnected && !address) {
      console.log('❌ No wallet connected');
    } else if (isConnected && !address) {
      console.log('⚠️ isConnected=true but no address - this might be the issue');
    }
  }, [isConnected, address, dispatch, shouldShowPopup]);

  // Auto-close popup if connection fails (user can try again)
  useEffect(() => {
    if (connectError && connectingWallet) {
      // Keep popup open on error so user can try again
      console.error('Connection error:', connectError);
    }
  }, [connectError, connectingWallet]);

  const handleConnect = async (connector: any) => {
    console.log('🚀 Starting connection process');
    console.log('Attempting to connect with:', connector.name, connector.id);
    console.log('Current state before connect:', { isConnected, address, connectingWallet });
    console.log('Setting connectingWallet to true');
    dispatch(connectWallet()); // Set connecting state to true
    
    try {
      console.log('Calling wagmi connect...');
      const result = await connect({ connector });
      console.log('✅ Connect function completed, result:', result);
      console.log('State after connect:', { isConnected, address });
    } catch (error) {
      console.error('❌ Connection failed:', error);
      // Reset connecting state on error
      dispatch(walletConnected());
    }
  };
  
  return (
    <Popup
      show={shouldShowPopup}
      close={() => dispatch(walletConnected())}
      header="Connect your wallet"
      forced={!isConnected}
    >
      <Buttons>
        <InjectedWallets>
          {connectors.filter(connector => {
            // Only include the injected connector, exclude everything else
            return connector.id === 'injected';
          }).map((connector) => {
            // Get a better display name for injected wallets
            const getDisplayName = (connector: any) => {
              if (connector.name.toLowerCase().includes('rabby')) {
                return 'Rabby';
              }
              if (connector.name.toLowerCase().includes('metamask')) {
                return 'MetaMask';
              }
              if (connector.id === 'injected') {
                // Auto-detect the wallet name from window.ethereum
                if (typeof window !== 'undefined' && window.ethereum) {
                  if ((window.ethereum as any).isRabby) return 'Rabby';
                  if ((window.ethereum as any).isMetaMask) return 'MetaMask';
                  if ((window.ethereum as any).isCoinbaseWallet) return 'Coinbase Wallet';
                }
                return 'Injected';
              }
              return connector.name;
            };

            return (
              <Button 
                key={connector.id}
                primary={false}
                click={() => {
                  console.log('Connecting with:', connector.name, connector.id);
                  handleConnect(connector);
                }}
              >
                {getDisplayName(connector)}
              </Button>
            );
          })}
        </InjectedWallets>
        
        <WalletConnectSection>
          {connectors.filter(connector => 
            connector.id === 'walletConnect'
          ).map((connector) => (
            <Button 
              key={connector.id}
              wide={true}
              click={() => {
                console.log('Connecting with WalletConnect:', connector.name, connector.id);
                handleConnect(connector);
              }}
            >
              WalletConnect
            </Button>
          ))}
        </WalletConnectSection>
      </Buttons>
    </Popup>
  );
};

export default WalletSelector;
